import { useCallback } from 'react';
import useVelocityStore from 'src/stores/VelocityStore/useVelocityStore';
import { useVisibleAccountOrders } from 'src/hooks/query/accountTradingData/useVisibleOrders';
import { MAX_ORDER_COUNT } from 'src/constants/constants';

const MIN_SCALED_ORDER_COUNT = 2;

interface Params {
	updateStateFromCustomInput: (value: string) => void;
	updateStateFromPreset: (value: string) => void;
	updateCustomInputValue: (value: string) => void;
}

export default function useScaledOrderCountInputHandlers({
	updateStateFromCustomInput,
	updateStateFromPreset,
	updateCustomInputValue,
}: Params) {
	const setVelocityStore = useVelocityStore((s) => s.set);

	const updateStore = useCallback(
		(value: number) => {
			setVelocityStore((s) => {
				s.tradeForm.scaledOrders.orderCount = value;
			});
		},
		[setVelocityStore]
	);

	// Expired orders still occupy an on-chain slot, but the placement flow cancels them
	// in the same transaction to reclaim their slots (see buildExpiredOrderCancelIx), so
	// the cap counts only visible (non-expired) orders — otherwise stale orders would
	// block scaling below what this flow can actually place.
	const openOrders = useVisibleAccountOrders();
	const openOrderCount = openOrders.length;

	const handleCustomInputChange = useCallback(
		(value: string) => {
			const parsedVal = parseInt(value, 10);

			//  If the value `isNaN` or less than the min, we skip updating the store so that
			//  the previous valid value remains. We also don't force-replace the user's input,
			// as we want to be able to e.g. fully delete the input or type in `1` -> `15`.
			if (isNaN(parsedVal) || parsedVal < MIN_SCALED_ORDER_COUNT) {
				updateCustomInputValue(value);
				return;
			}

			// If the value is greater than the remaining order count, we update it to the
			// remaining order count. Here, we do force-replace the user's input, as it seems
			// more acceptible in this case.
			const remainingOrderCount = MAX_ORDER_COUNT - openOrderCount;
			if (parsedVal > remainingOrderCount) {
				updateStateFromCustomInput(remainingOrderCount.toString());
				updateStore(remainingOrderCount);
				return;
			}

			// Otherwise, their value is valid and we can use it to update.
			updateStateFromCustomInput(value);
			updateStore(parsedVal);
		},
		[
			openOrderCount,
			updateStateFromCustomInput,
			updateCustomInputValue,
			updateStore,
		]
	);

	const handlePresetClick = useCallback(
		(value: number) => {
			updateStateFromPreset(value.toString());
			updateStore(value);
		},
		[updateStateFromPreset, updateStore]
	);

	return {
		handleCustomInputChange,
		handlePresetClick,
	};
}
